import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { VoorzieningItem } from '../../types';

// Voorzieningen: circle markers with category colors
const VOORZIENING_COLORS: Record<string, string> = {
  huisarts: '#ef4444',
  ziekenhuis: '#dc2626',
  apotheek: '#f97316',
  supermarkt: '#22c55e',
  basisschool: '#3b82f6',
  middelbare_school: '#6366f1',
  sportvoorziening: '#14b8a6',
  ov_halte: '#8b5cf6',
  gemeentehuis: '#64748b',
};

const VOORZIENING_LABELS: Record<string, string> = {
  huisarts: 'Huisarts',
  ziekenhuis: 'Ziekenhuis',
  apotheek: 'Apotheek',
  supermarkt: 'Supermarkt',
  basisschool: 'Basisschool',
  middelbare_school: 'Middelbare school',
  sportvoorziening: 'Sportvoorziening',
  ov_halte: 'OV halte',
  gemeentehuis: 'Gemeentehuis',
};

// Bedrijven: diamond markers with category colors
const BEDRIJF_COLORS: Record<string, string> = {
  detailhandel: '#f59e0b',
  horeca: '#f97316',
  industrie: '#6b7280',
  ambacht: '#a855f7',
  energie: '#eab308',
  logistiek: '#8b5cf6',
  zorg: '#ef4444',
  dienstverlening: '#3b82f6',
  agrarisch: '#22c55e',
};

const BEDRIJF_LABELS: Record<string, string> = {
  detailhandel: 'Detailhandel',
  horeca: 'Horeca',
  industrie: 'Industrie',
  ambacht: 'Ambacht',
  energie: 'Energie',
  logistiek: 'Logistiek',
  zorg: 'Zorg',
  dienstverlening: 'Dienstverlening',
  agrarisch: 'Agrarisch',
};

// Organisaties: square markers
const ORGANISATIE_COLORS: Record<string, string> = {
  scholen: '#3b82f6',
  zorginstellingen: '#ef4444',
  sportverenigingen: '#14b8a6',
  religieuze_instellingen: '#8b5cf6',
  culturele_organisaties: '#ec4899',
};

const ORGANISATIE_LABELS: Record<string, string> = {
  scholen: 'School',
  zorginstellingen: 'Zorginstelling',
  sportverenigingen: 'Sportvereniging',
  religieuze_instellingen: 'Religieuze instelling',
  culturele_organisaties: 'Culturele organisatie',
};

// Circle marker icon (voorzieningen)
function createCircleIcon(color: string) {
  return L.divIcon({
    className: 'gebiedsdata-marker',
    html: `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
      <circle cx="11" cy="11" r="9" fill="${color}" stroke="white" stroke-width="2"/>
    </svg>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -11],
  });
}

// Diamond marker icon (bedrijven)
function createDiamondIcon(color: string) {
  return L.divIcon({
    className: 'gebiedsdata-marker',
    html: `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="4" width="14" height="14" rx="2" fill="${color}" stroke="white" stroke-width="2" transform="rotate(45 11 11)"/>
    </svg>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -11],
  });
}

// Square marker icon (organisaties)
function createSquareIcon(color: string) {
  return L.divIcon({
    className: 'gebiedsdata-marker',
    html: `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="16" height="16" rx="3" fill="${color}" stroke="white" stroke-width="2"/>
    </svg>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -11],
  });
}

function createPopup(title: string, name: string, subcategory: string, distance: number, color: string) {
  return `
    <div style="font-family:'Open Sans',sans-serif;min-width:160px;">
      <strong style="font-size:13px;color:#323232;">${name}</strong>
      <span style="display:inline-block;margin-left:6px;padding:1px 6px;font-size:10px;color:white;background:${color};border-radius:10px;">${title}</span>
      <p style="font-size:12px;color:#666;margin:6px 0 0;">${subcategory} — ${distance} km</p>
    </div>
  `;
}

interface GebiedsdataLayerProps {
  voorzieningen: VoorzieningItem[] | null;
  bedrijven: VoorzieningItem[] | null;
  organisaties: VoorzieningItem[] | null;
  activeFilters: Record<string, boolean>;
  onMarkerClick?: (lat: number, lon: number) => void;
}

export const GebiedsdataLayer: React.FC<GebiedsdataLayerProps> = ({
  voorzieningen,
  bedrijven,
  organisaties,
  activeFilters,
  onMarkerClick,
}) => {
  const map = useMap();
  const markersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    // Clear existing markers
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    // Voorzieningen (circle markers)
    if (activeFilters.voorzieningen && voorzieningen) {
      voorzieningen.forEach(item => {
        if (!item.lat || !item.lon) return;
        const color = VOORZIENING_COLORS[item.category] || '#9DA6A8';
        const label = VOORZIENING_LABELS[item.category] || item.category;
        const marker = L.marker([item.lat, item.lon], { icon: createCircleIcon(color) })
          .bindPopup(createPopup(label, item.name, item.subcategory, item.distance, color))
          .addTo(map);
        if (onMarkerClick) {
          marker.on('click', () => onMarkerClick(item.lat, item.lon));
        }
        markersRef.current.push(marker);
      });
    }

    // Bedrijven (diamond markers)
    if (activeFilters.bedrijven_osm && bedrijven) {
      bedrijven.forEach(item => {
        if (!item.lat || !item.lon) return;
        const color = BEDRIJF_COLORS[item.category] || '#9DA6A8';
        const label = BEDRIJF_LABELS[item.category] || item.category;
        const marker = L.marker([item.lat, item.lon], { icon: createDiamondIcon(color) })
          .bindPopup(createPopup(label, item.name, item.subcategory, item.distance, color))
          .addTo(map);
        if (onMarkerClick) {
          marker.on('click', () => onMarkerClick(item.lat, item.lon));
        }
        markersRef.current.push(marker);
      });
    }

    // Organisaties (square markers)
    if (activeFilters.organisaties && organisaties) {
      organisaties.forEach(item => {
        if (!item.lat || !item.lon) return;
        const color = ORGANISATIE_COLORS[item.category] || '#9DA6A8';
        const label = ORGANISATIE_LABELS[item.category] || item.category;
        const marker = L.marker([item.lat, item.lon], { icon: createSquareIcon(color) })
          .bindPopup(createPopup(label, item.name, item.subcategory, item.distance, color))
          .addTo(map);
        if (onMarkerClick) {
          marker.on('click', () => onMarkerClick(item.lat, item.lon));
        }
        markersRef.current.push(marker);
      });
    }

    return () => {
      markersRef.current.forEach(m => map.removeLayer(m));
      markersRef.current = [];
    };
  }, [voorzieningen, bedrijven, organisaties, activeFilters, map, onMarkerClick]);

  return null;
};
